import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrders, generateOrderId } from '../../contexts/OrdersContext';
import CheckoutView, { OrderDetails } from './CheckoutView';
import OrderSuccessView from './OrderSuccessView';
import PaymentPendingView from './PaymentPendingView';

type DrawerStep = 'cart' | 'login-gate' | 'checkout' | 'payment-pending' | 'success';

const paymentMethodLabels: Record<string, string> = {
  card: 'Credit / Debit Card',
  mobile: 'Mobile Money',
  cash: 'Cash on Delivery',
  wallet: 'SipDash Wallet',
};

const stepTitles: Record<DrawerStep, string> = {
  cart: 'Your Cart',
  'login-gate': 'Sign In to Order',
  checkout: 'Checkout',
  'payment-pending': 'Order Submitted',
  success: 'Order Confirmed',
};

const stepSubtitles: Record<DrawerStep, (count: number) => string> = {
  cart: (c) => `${c} item${c !== 1 ? 's' : ''}`,
  'login-gate': () => 'Login required to continue',
  checkout: () => 'Delivery & Payment',
  'payment-pending': () => 'Awaiting confirmation',
  success: () => 'Sit tight!',
};

export default function CartDrawer() {
  const { items, isOpen, closeCart, totalCount, totalPrice, updateQty, removeItem, clearCart } = useCart();
  const toast = useToast();
  const { user, login } = useAuth();
  const { addOrder } = useOrders();
  const [step, setStep] = useState<DrawerStep>('cart');
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [currentOrderId, setCurrentOrderId] = useState<string>('');

  // Login gate state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const formatPrice = (price: number) => `GYD $${price.toLocaleString()}`;

  const handleClose = () => {
    closeCart();
    setTimeout(() => {
      if (step === 'payment-pending' || step === 'success') {
        clearCart();
      }
      setStep('cart');
    }, 400);
  };

  const handleRemoveItem = (id: string, name: string) => {
    removeItem(id);
    toast.info('Item removed', `${name} was removed from your cart.`);
  };

  const handleClearCart = () => {
    clearCart();
    toast.warning('Cart cleared', 'All items have been removed.');
  };

  const handlePlaceOrder = (details: OrderDetails) => {
    const orderId = generateOrderId();
    setCurrentOrderId(orderId);
    setOrderDetails(details);

    addOrder({
      id: orderId,
      customer: user?.name ?? 'Guest',
      phone: user?.phone ?? '',
      address: details.address,
      note: details.notes || undefined,
      items: items.map((i) => ({ name: i.name, qty: i.quantity, price: i.price * i.quantity })),
      total: totalPrice,
      status: 'payment_pending',
      paymentMethod: paymentMethodLabels[details.paymentMethod] ?? details.paymentMethod,
      createdAt: new Date().toISOString(),
      cashAmount: details.cashAmount,
      changeAmount: details.changeAmount,
      userId: user?.id,
    });

    setStep('payment-pending');
    toast.info('Order submitted!', 'Awaiting confirmation by our team.');
  };

  const handleSuccessClose = () => {
    clearCart();
    setStep('cart');
    closeCart();
  };

  const handleCheckoutClick = () => {
    if (!user) {
      setLoginEmail('');
      setLoginPassword('');
      setLoginError('');
      setStep('login-gate');
    } else {
      setStep('checkout');
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword.trim()) {
      setLoginError('Please fill in all fields.');
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    const result = await login(loginEmail.trim(), loginPassword);
    setLoginLoading(false);
    if (result.success) {
      toast.success('Welcome back!', 'You are now logged in.');
      setStep('checkout');
    } else {
      setLoginError(result.error ?? 'Invalid credentials.');
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        className={`fixed inset-0 bg-black/40 z-[60] transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-[440px] max-w-full bg-white z-[70] flex flex-col transition-transform ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ transitionDuration: '380ms' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            {(step === 'checkout' || step === 'login-gate') && (
              <button
                onClick={() => setStep('cart')}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer mr-1"
              >
                <i className="ri-arrow-left-line text-lg" />
              </button>
            )}
            <div
              className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 ${
                step === 'success'
                  ? 'bg-green-100'
                  : step === 'payment-pending'
                  ? 'bg-amber-100'
                  : step === 'login-gate'
                  ? 'bg-stone-100'
                  : 'bg-[#FF6B35]/10'
              }`}
            >
              <i
                className={`text-lg ${
                  step === 'success'
                    ? 'ri-checkbox-circle-line text-green-600'
                    : step === 'payment-pending'
                    ? 'ri-time-line text-amber-500'
                    : step === 'login-gate'
                    ? 'ri-user-line text-stone-500'
                    : step === 'checkout'
                    ? 'ri-secure-payment-line text-[#FF6B35]'
                    : step === 'cart'
                    ? 'ri-shopping-cart-2-line text-[#FF6B35]'
                    : 'ri-shopping-cart-2-line text-[#FF6B35]'
                }`}
              />
            </div>
            <div>
              <h2
                className="font-black text-gray-900 text-lg leading-none"
                style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}
              >
                {stepTitles[step]}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">{stepSubtitles[step](totalCount)}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">

          {/* CART STEP */}
          {step === 'cart' && (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
                    <div className="w-20 h-20 flex items-center justify-center bg-gray-50 rounded-full">
                      <i className="ri-shopping-cart-line text-4xl text-gray-300" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-700 text-base">Your cart is empty</p>
                      <p className="text-gray-400 text-sm mt-1">Add some beverages to get started!</p>
                    </div>
                    <button
                      onClick={handleClose}
                      className="bg-[#FF6B35] text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-[#e85d2a] transition-colors cursor-pointer whitespace-nowrap mt-2"
                    >
                      Browse Menu
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {items.map((item) => (
                      <div key={item.id} className="flex items-center gap-4 p-3 rounded-2xl bg-[#FAF8F5] group">
                        <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-white">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-900 text-sm truncate">{item.name}</p>
                          <p className="text-gray-400 text-xs">{item.volume}</p>
                          <p className="text-[#FF6B35] font-black text-sm mt-0.5">{formatPrice(item.price)}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <button
                            onClick={() => handleRemoveItem(item.id, item.name)}
                            className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-all cursor-pointer w-5 h-5 flex items-center justify-center"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                          <div className="flex items-center gap-2 bg-white rounded-full px-1 py-1 border border-gray-200">
                            <button
                              onClick={() => updateQty(item.id, -1)}
                              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-700 transition-colors cursor-pointer text-sm font-bold"
                            >
                              <i className="ri-subtract-line" />
                            </button>
                            <span className="text-sm font-black text-gray-900 w-4 text-center">{item.quantity}</span>
                            <button
                              onClick={() => updateQty(item.id, 1)}
                              className="w-6 h-6 flex items-center justify-center rounded-full bg-[#FF6B35] hover:bg-[#e85d2a] text-white transition-colors cursor-pointer text-sm"
                            >
                              <i className="ri-add-line" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {items.length > 0 && (
                <div className="px-6 py-5 border-t border-gray-100 bg-white flex-shrink-0">
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center text-green-500">
                        <i className="ri-motorbike-line" />
                      </div>
                      <span>Delivery to Georgetown</span>
                    </div>
                    <span className="font-semibold text-green-600">FREE</span>
                  </div>
                  <div className="space-y-2 mb-5">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Subtotal ({totalCount} items)</span>
                      <span>{formatPrice(totalPrice)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Delivery fee</span>
                      <span className="text-green-600 font-semibold">GYD $0</span>
                    </div>
                    <div className="border-t border-gray-100 pt-2 flex justify-between font-black text-gray-900">
                      <span>Total</span>
                      <span className="text-[#FF6B35]">{formatPrice(totalPrice)}</span>
                    </div>
                  </div>
                  <button
                    onClick={handleCheckoutClick}
                    className="w-full bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-secure-payment-line" />
                    </div>
                    Checkout — {formatPrice(totalPrice)}
                  </button>
                  <button
                    onClick={handleClearCart}
                    className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-3 cursor-pointer transition-colors"
                  >
                    Clear cart
                  </button>
                </div>
              )}
            </>
          )}

          {/* LOGIN GATE STEP */}
          {step === 'login-gate' && (
            <div className="flex-1 overflow-y-auto px-6 py-6 flex flex-col">
              {/* Illustration */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-20 h-20 bg-[#FFF5F0] rounded-full flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-[#FF6B35]/20 rounded-full flex items-center justify-center">
                    <i className="ri-lock-password-line text-2xl text-[#FF6B35]" />
                  </div>
                </div>
                <h3 className="text-lg font-black text-gray-900" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
                  Login to Continue
                </h3>
                <p className="text-sm text-gray-400 mt-1 max-w-[260px]">
                  You need an account to place an order. It only takes a moment!
                </p>
              </div>

              {/* Login form */}
              <form onSubmit={handleLoginSubmit} className="space-y-4 flex-1">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Email or Phone
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-user-line text-sm" />
                    </span>
                    <input
                      type="text"
                      value={loginEmail}
                      onChange={(e) => { setLoginEmail(e.target.value); setLoginError(''); }}
                      placeholder="Email or phone number"
                      className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm bg-[#fafaf8] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Password
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-lock-line text-sm" />
                    </span>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={loginPassword}
                      onChange={(e) => { setLoginPassword(e.target.value); setLoginError(''); }}
                      placeholder="••••••••"
                      className="w-full pl-9 pr-10 py-3 border border-gray-200 rounded-xl text-sm bg-[#fafaf8] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={showPassword ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
                    </button>
                  </div>
                </div>

                {loginError && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                    <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0" />
                    <p className="text-xs text-red-600">{loginError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full bg-[#FF6B35] hover:bg-[#e85d2a] disabled:opacity-60 text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap mt-2"
                >
                  {loginLoading ? (
                    <><i className="ri-loader-4-line animate-spin" /> Signing in...</>
                  ) : (
                    <><i className="ri-login-box-line" /> Sign In &amp; Continue</>
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs text-gray-400">Don&apos;t have an account?</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              {/* Register link */}
              <Link
                to="/auth/register"
                onClick={handleClose}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-gray-200 hover:border-[#FF6B35] text-gray-700 hover:text-[#FF6B35] font-semibold text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-add-line" />
                Create an Account
              </Link>

              <p className="text-center text-xs text-gray-400 mt-4">
                Your cart is saved — login and your items will still be here.
              </p>
            </div>
          )}

          {step === 'checkout' && (
            <CheckoutView
              items={items}
              totalPrice={totalPrice}
              onBack={() => setStep('cart')}
              onPlaceOrder={handlePlaceOrder}
            />
          )}

          {step === 'payment-pending' && orderDetails && (
            <PaymentPendingView
              orderId={currentOrderId}
              total={totalPrice}
              items={items}
              details={orderDetails}
              onClose={handleSuccessClose}
            />
          )}

          {step === 'success' && orderDetails && (
            <OrderSuccessView
              total={totalPrice}
              details={orderDetails}
              onClose={handleSuccessClose}
            />
          )}
        </div>
      </div>
    </>
  );
}
